import { useId, useState, type FC } from 'react';
import { Link } from '@tanstack/react-router';
import type {
  ForecastLineDto,
  ForecastShipmentDto,
  ShipmentForecastDto,
  WarehouseDto,
} from '@/api';
import { Badge, Button, Checkbox, Empty, Input, Select } from '@/ui';

import { addDays } from '../../lib/dates';
import {
  formatDate,
  formatDecimalInput,
  formatMoney,
  toNumber,
} from '../../lib/format';
import { parseDecimal, parseInterval } from '../shipment-actions';
import {
  REASON_LABELS,
  applySelection,
  describeBasis,
  groupByClient,
  isEntrySelected,
  listEntries,
  resolveGroupState,
  type ForecastEntry,
  type ForecastPatch,
  type ForecastSelection,
} from './lib/forecast';

/*
 * Шаг «Предложения» мастера плана из истории: отгрузки по клиентам с галочками
 * на строке, отгрузке и клиенте. Дата и склад правятся у отгрузки, количество и
 * интервал - у строки; правка уходит на сервер и сдвигает следующие повторы
 * товара (plan/shipment-plan-from-history, «Как считаем предложения»).
 */

export interface ForecastProposalsProps {
  forecast: ShipmentForecastDto;
  selection: ForecastSelection;
  onSelectionChange: (selection: ForecastSelection) => void;
  onEdit: (entries: ReadonlyArray<ForecastEntry>, patch: ForecastPatch) => void;
  warehouses: ReadonlyArray<WarehouseDto>;
  onExclude: (partnerId: string) => void;
  isExcluding: boolean;
  isRecomputing: boolean;
}

const LINE_GRID =
  'grid grid-cols-[16px_minmax(0,1fr)_92px_104px_150px] items-start gap-2.5';

const formatShipments = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return `${count} отгрузка`;
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return `${count} отгрузки`;
  return `${count} отгрузок`;
};

export const ForecastProposals: FC<ForecastProposalsProps> = ({
  forecast,
  selection,
  onSelectionChange,
  onEdit,
  warehouses,
  onExclude,
  isExcluding,
  isRecomputing,
}) => {
  const selectAllId = useId();
  const allEntries = listEntries(forecast.shipments);
  const all = resolveGroupState(selection, allEntries);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-ink-600">
        <span className="inline-flex items-center gap-2">
          <Checkbox
            id={selectAllId}
            checked={all.isChecked}
            indeterminate={all.isIndeterminate}
            disabled={allEntries.length === 0}
            onChange={(event) =>
              onSelectionChange(
                applySelection(selection, allEntries, event.target.checked),
              )
            }
          />
          <label htmlFor={selectAllId} className="font-medium text-ink-800">
            Выбрать все
          </label>
        </span>
        <span>
          Горизонт {String(forecast.horizonMonths)} мес., история{' '}
          {String(forecast.historyMonths)} мес. -{' '}
          <Link to="/settings" className="text-brand-green-700 hover:underline">
            настройки
          </Link>
        </span>
        {isRecomputing && (
          <span className="text-ink-500">Пересчитываем...</span>
        )}
      </div>

      {forecast.shipments.length === 0 ? (
        <Empty>
          Предлагать нечего: у выбранных клиентов нет повторяющихся поставок или
          следующие уже стоят в плане.
        </Empty>
      ) : (
        groupByClient(forecast.shipments).map((client) => {
          const entries = listEntries(client.shipments);
          const state = resolveGroupState(selection, entries);

          return (
            <section
              key={client.partnerId}
              className="rounded-lg border border-ink-200 bg-white"
            >
              <header className="flex flex-wrap items-center gap-2.5 rounded-t-lg border-b border-ink-150 bg-ink-50 px-3 py-2">
                <Checkbox
                  aria-label={`Все отгрузки клиента ${client.partnerName}`}
                  checked={state.isChecked}
                  indeterminate={state.isIndeterminate}
                  onChange={(event) =>
                    onSelectionChange(
                      applySelection(selection, entries, event.target.checked),
                    )
                  }
                />
                <b className="text-[13.5px] font-semibold text-ink-900">
                  {client.partnerName}
                </b>
                <span className="text-[12px] text-ink-500">
                  {formatShipments(client.shipments.length)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  disabled={isExcluding}
                  title="Клиент больше не попадёт в план из истории; вернуть - в «Клиентах»"
                  onClick={() => onExclude(client.partnerId)}
                >
                  Исключить из планирования
                </Button>
              </header>

              {client.shipments.map((shipment) => (
                <ShipmentBlock
                  key={`${shipment.plannedDate}-${shipment.warehouseId ?? 'none'}`}
                  shipment={shipment}
                  minDate={forecast.asOf}
                  selection={selection}
                  onSelectionChange={onSelectionChange}
                  onEdit={onEdit}
                  warehouses={warehouses}
                />
              ))}
            </section>
          );
        })
      )}
    </div>
  );
};

interface ShipmentBlockProps {
  shipment: ForecastShipmentDto;
  minDate: string;
  selection: ForecastSelection;
  onSelectionChange: (selection: ForecastSelection) => void;
  onEdit: (entries: ReadonlyArray<ForecastEntry>, patch: ForecastPatch) => void;
  warehouses: ReadonlyArray<WarehouseDto>;
}

/** Одна предложенная отгрузка: дата и склад - на все её строки сразу. */
const ShipmentBlock: FC<ShipmentBlockProps> = ({
  shipment,
  minDate,
  selection,
  onSelectionChange,
  onEdit,
  warehouses,
}) => {
  const entries = listEntries([shipment]);
  const state = resolveGroupState(selection, entries);
  const dateLabel = formatDate(shipment.plannedDate);

  return (
    <div className="border-b border-ink-150 px-3 py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2.5">
        <Checkbox
          aria-label={`Отгрузка ${dateLabel}`}
          checked={state.isChecked}
          indeterminate={state.isIndeterminate}
          onChange={(event) =>
            onSelectionChange(
              applySelection(selection, entries, event.target.checked),
            )
          }
        />
        <Input
          type="date"
          aria-label={`Дата отгрузки ${dateLabel}`}
          min={minDate}
          value={shipment.plannedDate}
          onChange={(event) => {
            if (event.target.value !== '')
              onEdit(entries, { plannedDate: event.target.value });
          }}
          className="h-8 w-[150px]"
        />
        <Select
          aria-label={`Склад отправки, отгрузка ${dateLabel}`}
          value={shipment.warehouseId ?? ''}
          onChange={(event) => {
            if (event.target.value !== '')
              onEdit(entries, { warehouseId: event.target.value });
          }}
          className="h-8 max-w-[260px]"
        >
          {shipment.warehouseId == null && (
            <option value="">Склад неизвестен</option>
          )}
          {warehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>
              {warehouse.name}
            </option>
          ))}
        </Select>
      </div>

      <div
        className={`${LINE_GRID} mt-2.5 text-[11px] tracking-[0.05em] text-ink-500 uppercase`}
      >
        <span />
        <span>Товар</span>
        <span>Кол-во</span>
        <span className="text-right">Цена без НДС</span>
        <span>Следующая через</span>
      </div>

      <div className="mt-1.5 flex flex-col gap-2">
        {shipment.lines.map((line) => (
          <LineRow
            key={`${line.productId}-${String(line.occurrence)}`}
            entry={{ partnerId: shipment.partnerId, line }}
            plannedDate={shipment.plannedDate}
            isSelected={isEntrySelected(selection, {
              partnerId: shipment.partnerId,
              line,
            })}
            onSelectedChange={(isSelected) =>
              onSelectionChange(
                applySelection(
                  selection,
                  [{ partnerId: shipment.partnerId, line }],
                  isSelected,
                ),
              )
            }
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
};

interface LineRowProps {
  entry: ForecastEntry;
  plannedDate: string;
  isSelected: boolean;
  onSelectedChange: (isSelected: boolean) => void;
  onEdit: (entries: ReadonlyArray<ForecastEntry>, patch: ForecastPatch) => void;
}

const LineRow: FC<LineRowProps> = ({
  entry,
  plannedDate,
  isSelected,
  onSelectedChange,
  onEdit,
}) => {
  const { line } = entry;
  const interval = toNumber(line.nextShipmentIntervalDays);

  return (
    <div className={`${LINE_GRID} text-[12.5px]`}>
      <Checkbox
        aria-label={`${line.productName}, ${formatDate(plannedDate)}`}
        checked={isSelected}
        onChange={(event) => onSelectedChange(event.target.checked)}
        className="mt-2"
      />
      <div className="min-w-0">
        <div className="truncate text-ink-900" title={line.productName}>
          {line.productName}
        </div>
        <div className="mt-0.5 text-[11.5px] text-ink-500">
          {describeBasis(line)}
        </div>
        <LineBadges line={line} />
      </div>
      <CommitInput
        key={String(line.quantity)}
        aria-label={`Количество, ${line.productName}`}
        value={formatDecimalInput(line.quantity)}
        parse={(text) => {
          const quantity = parseDecimal(text);
          return quantity !== null && quantity > 0 ? quantity : null;
        }}
        onCommit={(quantity) => onEdit([entry], { quantity })}
      />
      <span className="pt-2 text-right whitespace-nowrap text-ink-700 tabular-nums">
        {line.price == null ? (
          <span className="text-st-approval">нет цены</span>
        ) : (
          formatMoney(toNumber(line.price))
        )}
      </span>
      <div className="flex items-center gap-1.5">
        <CommitInput
          key={String(interval)}
          aria-label={`Следующая отгрузка через, дн., ${line.productName}`}
          value={String(interval)}
          parse={(text) => {
            const days = parseInterval(text);
            return days !== null && !Number.isNaN(days) ? days : null;
          }}
          onCommit={(days) =>
            onEdit([entry], { nextShipmentIntervalDays: days })
          }
          className="w-14"
        />
        <span className="text-ink-500">дн.</span>
        <span className="text-[11.5px] text-ink-600">
          {formatDate(addDays(plannedDate, interval))}
        </span>
      </div>
    </div>
  );
};

/** Причина без галочки и пометка о просрочке. */
const LineBadges: FC<{ line: ForecastLineDto }> = ({ line }) => {
  if (line.reason == null && !line.isOverdue) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {line.reason != null && (
        <Badge tone="approval">{REASON_LABELS[line.reason]}</Badge>
      )}
      {line.isOverdue && (
        <Badge
          tone="delayed"
          title="Срок по ритму прошёл - строка перенесена на завтра"
        >
          просрочено
        </Badge>
      )}
    </div>
  );
};

interface CommitInputProps {
  value: string;
  parse: (text: string) => number | null;
  onCommit: (value: number) => void;
  className?: string;
  'aria-label': string;
}

/**
 * Поле, которое отдаёт значение, только когда менеджер закончил ввод: по уходу
 * фокуса или Enter. Каждое значение - пересчёт на сервере, поэтому не на каждый
 * символ. Негодное значение откатывается к прежнему. Новое значение с сервера
 * сбрасывает поле: родитель меняет его key.
 */
const CommitInput: FC<CommitInputProps> = ({
  value,
  parse,
  onCommit,
  className,
  'aria-label': ariaLabel,
}) => {
  const [text, setText] = useState(value);

  const commit = () => {
    const parsed = parse(text);

    if (parsed === null) {
      setText(value);
      return;
    }

    if (parsed !== parse(value)) onCommit(parsed);
  };

  return (
    <Input
      aria-label={ariaLabel}
      inputMode="decimal"
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      }}
      className={`h-8 px-2 text-[12.5px] ${className ?? ''}`}
    />
  );
};
